import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useAppStore } from '@stores'
import { useControlStore, useModelStore } from '@editorStores'
import { useSceneElement } from '@editorHooks'
import { tabConfig, windAngleToDirection } from '../../../RoofAssignment/constants'
import { buildSlabToStoreyMapping } from '../FormFields/utils'
import ManualWindLoadsSceneMarkers from './index'
import { buildHorizontalWindLoadArrows } from './utils'

interface Props {
  activeWindAngle?: WindAngle | null
}

const useManualWindLoadsSceneMarkers = ({ activeWindAngle }: Props): void => {
  const { watch } = useFormContext<WindLoadsFormValues>()
  const model = useModelStore(state => state.model)
  const loadDisplayScale = useAppStore(state => state.loadDisplayScale)
  const showHorizontalWindLoads = useControlStore(
    state => state.structuralPlanningElementVisibility.horizontalWindLoads,
  )

  const activeDirection = windAngleToDirection[activeWindAngle || tabConfig[0]]
  const results = watch('results')
  const ceilingLoads = results?.wind_loads_per_ceiling?.[activeDirection]
  const roofLoads = results?.wind_loads_roof?.[activeDirection]

  const slabToStorey = useMemo(() => buildSlabToStoreyMapping(model.slabs), [model.slabs])

  const loads = useMemo(
    () =>
      buildHorizontalWindLoadArrows({
        ceilingLoads: ceilingLoads ?? [],
        roofLoads: roofLoads ?? [],
        slabToStorey,
        storeyBoundaries: model.storey_boundaries,
        loadDisplayScale,
      }),
    [ceilingLoads, roofLoads, slabToStorey, model.storey_boundaries, loadDisplayScale],
  )

  const sceneElementProperties = useMemo(
    () => ({
      loads,
      activeWindAngle: activeWindAngle || tabConfig[0],
    }),
    [loads, activeWindAngle],
  )

  useSceneElement({
    enabled: showHorizontalWindLoads && loads.length > 0,
    component: ManualWindLoadsSceneMarkers,
    properties: sceneElementProperties,
  })
}

export default useManualWindLoadsSceneMarkers
